import { Box, Stack, Typography } from '@mui/material';
import useLocalization from '../../../hooks/useLocalization';
import type { ZeroXWithdrawalPreview } from '../../../schemas/zeroXWithdrawal';
import { zeroXCashierStyles as styles } from '../../../styles/components/zeroXCashier';
import { mergeSx } from '../../../utils/styles';
import { FormattedAmount } from '../../atoms/FormattedAmount';

type ZeroXWithdrawalSummaryProps = {
  quote: Pick<
    ZeroXWithdrawalPreview,
    | 'debit'
    | 'accountCurrency'
    | 'currency'
    | 'assetAmount'
    | 'estimatedNetworkFee'
    | 'estimatedReceived'
    | 'address'
    | 'tag'
  >;
};

const ZeroXWithdrawalSummary = ({ quote }: ZeroXWithdrawalSummaryProps) => {
  const { t } = useLocalization();

  return (
    <Stack sx={styles.summary}>
      <Stack sx={styles.received}>
        <Typography sx={styles.label}>{t('zeroXEstimatedReceived')}</Typography>

        <FormattedAmount
          value={quote.estimatedReceived}
          unit={quote.currency}
          currencyProps={{ sx: styles.asset }}
          sx={styles.amount}
        />
      </Stack>

      <Stack sx={styles.breakdown}>
        <Box component='dl' sx={styles.details}>
          <Box sx={mergeSx(styles.row, styles.debitRow)}>
            <Typography component='dt' sx={styles.debitTerm}>
              {t('zeroXAccountDebit')}
            </Typography>
            <FormattedAmount
              component='dd'
              value={quote.debit}
              currency={quote.accountCurrency}
              sx={styles.debitValue}
            />
          </Box>

          <Box sx={styles.row}>
            <Typography component='dt' sx={styles.term}>
              {t('zeroXGrossCrypto')}
            </Typography>
            <FormattedAmount
              component='dd'
              value={quote.assetAmount}
              unit={quote.currency}
              currencyProps={{ sx: styles.valueUnit }}
              sx={styles.value}
            />
          </Box>

          <Box sx={styles.row}>
            <Typography component='dt' sx={styles.term}>
              {t('zeroXEstimatedFee')}
            </Typography>
            <FormattedAmount
              component='dd'
              value={quote.estimatedNetworkFee}
              unit={quote.currency}
              currencyProps={{ sx: styles.valueUnit }}
              sx={styles.value}
            />
          </Box>
        </Box>

        <Typography sx={styles.feeHelp}>{t('zeroXFeeEstimateNotice')}</Typography>
      </Stack>

      <Box component='dl' sx={styles.destinationDetails}>
        <Box sx={styles.destination}>
          <Typography component='dt' sx={styles.label}>
            {t('zeroXCurrency')}
          </Typography>
          <Typography component='dd' sx={styles.destinationValue}>
            {quote.currency}
          </Typography>
        </Box>

        <Box sx={styles.destination}>
          <Typography component='dt' sx={styles.label}>
            {t('walletAddress')}
          </Typography>
          <Typography component='dd' sx={mergeSx(styles.destinationValue, styles.addressValue)}>
            {quote.address}
          </Typography>
        </Box>

        {quote.tag && (
          <Box sx={styles.destination}>
            <Typography component='dt' sx={styles.label}>
              {t('zeroXDestinationTag')}
            </Typography>
            <Typography component='dd' sx={mergeSx(styles.destinationValue, styles.memoValue)}>
              {quote.tag}
            </Typography>
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default ZeroXWithdrawalSummary;
